import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { CLINIC, LOGO_SVG, doctorDisplay, buildWhatsAppBrandedMessage } from "../lib/clinic";
import { openWhatsApp } from "../lib/whatsapp";

export default function ReferralPrint() {
  const { refId } = useParams();
  const nav = useNavigate();
  const [ref, setRef] = useState(null);
  const [patient, setPatient] = useState(null);
  const [visit, setVisit] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/referrals/${refId}`);
        setRef(r.data);
        const p = await api.get(`/patients/${r.data.patient_id}`);
        setPatient(p.data.patient);
        const v = await api.get(`/visits/${r.data.visit_id}`);
        setVisit(v.data.visit);
      } catch { toast.error("Failed to load referral"); }
    })();
  }, [refId]);

  const share = () => {
    const dateStr = new Date(ref.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const toLine = ref.specialist_name
      ? `To: ${/^dr\.?\s/i.test(ref.specialist_name) ? ref.specialist_name : `Dr. ${ref.specialist_name}`}${ref.specialty ? ` (${ref.specialty})` : ""}`
      : "To: Colleague Specialist";
    const body = [
      toLine,
      ref.clinic_or_hospital ? `Institution: ${ref.clinic_or_hospital}` : "",
      "",
      `Referral ID: ${ref.referral_id}`,
      `Urgency: ${ref.urgency || "Routine"}`,
      ref.reason ? `Reason: ${ref.reason}` : "",
      "",
      "I am referring the above patient for your expert opinion and further management. Kindly evaluate and advise.",
    ].filter(Boolean).join("\n");
    const msg = buildWhatsAppBrandedMessage({
      lang: "en",
      title: "Specialist Referral Letter",
      body,
      patientName: patient?.name,
      kind: "education",
      dateStr,
    });
    openWhatsApp(patient?.mobile, msg);
  };

  if (!ref || !patient) return <div className="p-10 text-slate-500">Loading…</div>;

  const urgencyCls = ref.urgency === "Urgent" ? "bg-red-100 text-red-700 border-red-300" : ref.urgency === "Semi-urgent" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-slate-100 text-slate-600 border-slate-300";

  return (
    <div className="min-h-screen bg-slate-100 print-shell">
      <Toaster position="top-right" richColors />
      <div className="no-print bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => nav(-1)} className="p-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm" data-testid="referral-back"><ArrowLeft size={14} /> Back</button>
        <div className="flex gap-2">
          <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="referral-print-btn"><Printer size={14} /> Print / Save PDF</Button>
          <Button onClick={share} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="referral-whatsapp-btn"><MessageCircle size={14} /> WhatsApp</Button>
        </div>
      </div>

      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page">
        <div className="p-8">
          <header className="flex justify-between items-start pb-4 border-b-2 border-slate-900">
            <div className="flex gap-4 items-start">
              <div className="w-16 h-20 flex-shrink-0">{LOGO_SVG}</div>
              <div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{CLINIC.qualifications}</div>
                <div className="text-[10px] text-slate-500">{CLINIC.regNo}</div>
                <div className="text-[10px] text-slate-500 mt-1 max-w-[280px] leading-snug">{CLINIC.address}</div>
              </div>
            </div>
            <div className="text-right">
              <div className={`inline-block px-3 py-1 rounded border text-xs font-semibold uppercase tracking-wider ${urgencyCls}`} data-testid="referral-urgency">
                {ref.urgency || "Routine"}
              </div>
              <div className="text-[11px] text-slate-600 mt-2 tabular">Referral ID · {ref.referral_id}</div>
              <div className="text-[11px] text-slate-500 tabular">{new Date(ref.created_at).toLocaleDateString()}</div>
            </div>
          </header>

          <div className="mt-6 text-sm">
            <div className="uppercase text-[10px] tracking-wider text-slate-500 font-semibold">Letter of Referral</div>
            <div className="mt-1 font-heading text-xl font-semibold text-slate-900">
              To {ref.specialist_name ? (/^dr\.?\s/i.test(ref.specialist_name) ? ref.specialist_name : `Dr. ${ref.specialist_name}`) : "—"}
              {ref.specialty && <span className="text-slate-500 font-normal text-base"> — {ref.specialty}</span>}
            </div>
            {ref.clinic_or_hospital && <div className="text-xs text-slate-600 mt-1">{ref.clinic_or_hospital}</div>}
          </div>

          <section className="grid grid-cols-4 gap-2 text-[11px] mt-6 py-3 border-y border-slate-200">
            <div><span className="uppercase text-slate-500">Patient:</span> <span className="font-medium">{patient.name}</span></div>
            <div><span className="uppercase text-slate-500">Age/Sex:</span> {patient.age} / {patient.sex}</div>
            <div><span className="uppercase text-slate-500">ID:</span> <span className="tabular">{patient.patient_id}</span></div>
            <div><span className="uppercase text-slate-500">Mobile:</span> {patient.mobile}</div>
          </section>

          <section className="mt-4 text-sm leading-relaxed">
            <p>{ref.specialist_name ? `Dear ${/^dr\.?\s/i.test(ref.specialist_name) ? ref.specialist_name : `Dr. ${ref.specialist_name}`},` : "Dear Colleague,"}</p>
            <p className="mt-3">
              I am referring the above patient for your expert opinion and further management.
            </p>
            {ref.reason && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reason for Referral</div>
                <div className="text-sm mt-1">{ref.reason}</div>
              </div>
            )}
          </section>

          <section className="mt-6 border border-slate-200 rounded p-4 text-[12px] leading-relaxed">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Clinical Summary</div>

            {visit?.diagnosis?.primary && (
              <div className="mb-2"><span className="text-slate-500">Diagnosis:</span> <span className="font-medium">{visit.diagnosis.primary}</span>{visit.diagnosis.secondary && <span className="text-slate-600"> · {visit.diagnosis.secondary}</span>}{visit.diagnosis.icd10 && <span className="text-slate-500 tabular text-[11px] ml-2">[{visit.diagnosis.icd10}]</span>}</div>
            )}
            {visit?.chief_complaints && <div className="mb-2"><span className="text-slate-500">Chief complaints:</span> {visit.chief_complaints}</div>}
            {visit?.known_case_of?.length > 0 && <div className="mb-2"><span className="text-slate-500">Known case of:</span> {visit.known_case_of.join(", ")}</div>}
            {visit?.allergy_flags?.length > 0 && <div className="mb-2 text-red-700"><span className="text-red-500">Alerts:</span> {visit.allergy_flags.join(", ")}</div>}
            {visit?.vitals && (
              <div className="mb-2 tabular">
                <span className="text-slate-500">Vitals:</span> BP {visit.vitals.sbp}/{visit.vitals.dbp} · Pulse {visit.vitals.pulse || "-"} · BMI {visit.vitals.bmi || "-"} · SpO₂ {visit.vitals.spo2 || "-"} · RBS {visit.vitals.random_sugar || "-"}
              </div>
            )}
            {ref.clinical_summary && (
              <div className="mt-3 whitespace-pre-line">{ref.clinical_summary}</div>
            )}
          </section>

          <p className="mt-6 text-sm">
            Kindly evaluate and advise further. I would be grateful for a follow-up note on your findings and
            recommendations. Please do not hesitate to contact the clinic for any additional information.
          </p>
          <p className="mt-4 text-sm">Thank you for your attention.</p>

          <div className="mt-10 flex justify-end">
            <div className="text-right">
              <div className="border-t border-slate-800 pt-1 text-[11px]">
                <div className="font-medium">{doctorDisplay(CLINIC.doctorName)}</div>
                <div className="text-slate-500">Referring Physician</div>
              </div>
            </div>
          </div>

          <footer className="mt-6 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed">
            This referral is generated by {CLINIC.name}. The referring doctor remains responsible for the accuracy
            of the clinical summary provided above. Please verify against the patient's records where necessary.
          </footer>
        </div>
      </div>
    </div>
  );
}
